import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Grid } from '@mui/material';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';

import vehicleActions from '../../../actions/vehicles';
import wranglerActions from '../../../actions/wranglers';
import taskActions from '../../../actions/tasks';
import * as snackbarActions from '../../../actions/snackbar';

import WarnBeforeDeleting from '../../common/warn-before-deleting';
import CreateEdit from './create-edit-task-modal';
import {
  shouldFetchList,
  pickupTaskReasonNames,
  automatedPickupTaskReasonNames,
  inspectTaskReasonNames,
  sortByIntegerString,
  TaskStatuses,
} from '../../../lib/utils';

function TaskFormContainer(props) {
  const {
    task,
    editing,
    fetchVehicles,
    fetchWranglers,
    clearOneTask,
    selectedMarketId,
    wranglers,
    vehicles,
    updateTask,
    createTask,
    createTasksForVehicles,
    showErrorSnackbar,
    showSuccessSnackbar,
    handleExtraSave,
    bulk,
    lockVehicleField,
  } = props;
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateEditModal, setShowCreateEditModal] = useState(false);
  useEffect(() => {
    const query = {
      market_id: selectedMarketId,
    };
    if (shouldFetchList(selectedMarketId, vehicles)) fetchVehicles(query);
    if (shouldFetchList(selectedMarketId, wranglers)) fetchWranglers(query);
  });

  if (task && !showCreateEditModal) {
    setShowCreateEditModal(true);
  }

  const pickupOptions = pickupTaskReasonNames.map((reason) => ({
    value: reason,
    label: reason,
    isDisabled: automatedPickupTaskReasonNames.includes(reason),
  }));
  const inspectOptions = inspectTaskReasonNames.map((reason) => ({
    value: reason,
    label: reason,
  }));
  const sortedVehicles = sortByIntegerString(vehicles);
  const vehicleOptions = sortedVehicles.map((vehicle) => ({
    value: vehicle._id,
    label: vehicle.humanId,
  }));
  const sortedWranglers = sortBy(wranglers, (wrangler) => wrangler.firstName.toLowerCase());
  const wranglerOptions = sortedWranglers.map((wrangler) => ({
    value: wrangler._id,
    label: `${wrangler.firstName} ${wrangler.lastName}`,
  }));

  const afterClose = (afterSave, saveCount = '', failMsg = '') => {
    setShowCreateEditModal(false);
    clearOneTask();
    if (afterSave) {
      const query = {
        market_id: selectedMarketId,
      };
      fetchVehicles(query);
      showSuccessSnackbar(`Successfully saved ${saveCount} task(s).  ${failMsg}`);
    }
  };
  return (
    <Grid container>
      <CreateEdit
        vehicles={sortedVehicles}
        wranglers={wranglers}
        task={task}
        open={showCreateEditModal}
        marketId={selectedMarketId}
        editing={editing}
        pickupOptions={pickupOptions}
        inspectOptions={inspectOptions}
        vehicleOptions={vehicleOptions}
        wranglerOptions={wranglerOptions}
        handleDelete={setIsDeleting}
        bulk={bulk}
        lockVehicleField={lockVehicleField}
        handleCloseModal={() => afterClose(false)}
        handleSave={(taskData) => {
          const { pickupReason, inspectReason, assignedVehicleIds } = taskData;
          // validation
          if (!pickupReason && !inspectReason) return showErrorSnackbar('task reason is required');

          if (assignedVehicleIds.length === 1 && !assignedVehicleIds[0]) return showErrorSnackbar('vehicle is required');

          if (bulk) {
            const vehicleIds = get(task, 'assignedVehicleIds', []);
            createTasksForVehicles(taskData, vehicleIds)
              .then((data) => {
                const successCount = get(data, 'data.tasks.length', 0);
                const fails = get(data, 'data.fails.length', 0);
                let failMsg = '';
                if (fails) failMsg = `No tasks were created for ${fails} vehicles because tasks already existed.  Please edit the existing task.`;
                afterClose(true, successCount, failMsg);
              });
          } else {
            if (assignedVehicleIds.length < 1) return showErrorSnackbar('assigned vehicle is required');

            const updateFunction = editing ? updateTask : createTask;
            updateFunction(taskData, taskData._id)
              .then((data) => {
                const savedTask = get(data, 'data', {});
                afterClose(true);
                // for create instances that need additional processing
                // (like saving a case after you create a task on it)
                handleExtraSave && handleExtraSave(savedTask, editing);
              })
              .catch((e) => {
                showErrorSnackbar('Error saving task', e);
              });
          }
        }}
      />
      <WarnBeforeDeleting
        isOpen={isDeleting}
        message="Are you sure you want to delete this task?"
        onCancel={() => setIsDeleting(false)}
        onConfirm={() => {
          const updatedTask = {
            ...task,
            status: TaskStatuses.DELETED,
          };
          updateTask(updatedTask, updatedTask._id)
            .then(() => afterClose(true))
            .catch(() => showErrorSnackbar('error deleting task'));
        }}
      />
    </Grid>
  );
}

function mapStateToProps({ vehicles, wranglers }, { match }) {
  const marketId = get(match, 'params.marketId');
  return {
    selectedMarketId: marketId,
    wranglers: wranglers.marketWranglers,
    vehicles: vehicles.marketVehicles,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    ...taskActions,
    ...vehicleActions,
    ...wranglerActions,
    ...snackbarActions,
  }, dispatch);
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(TaskFormContainer));
